import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProjectService } from '../services/ProjectService';
import {
    Loader2, Sparkles, Play, Dices, Plus, Trash2, Copy, X, FileText, Mic, Settings, Volume2
} from 'lucide-react';
import './RantCreatorPage.css';
import { RantEditorPage } from './RantEditorPage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Available voices (ElevenLabs) with preview URLs
const VOICES = [
    // Male voices
    { id: 'adam', label: 'Adam', description: 'Deep, authoritative male voice', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/pNInz6obpgDQGcFmaJgB/d6905d7a-dd26-4187-bfff-1bd3a5ea7cac.mp3' },
    { id: 'brian', label: 'Brian', description: 'Deep male voice, great for narration', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/nPczCjzI2devNBz1zQrb/2dd3e72c-4fd3-42f1-93ea-abc5d4e5aa1d.mp3' },
    { id: 'liam', label: 'Liam', description: 'Young male voice, natural narration', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/TX3LPaxmHKxFdv7VOQHJ/63148076-6363-42db-aea8-31424308b92c.mp3' },
    // Female voices
    { id: 'jessica', label: 'Jessica', description: 'Young, expressive female voice', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/cgSgspJ2msm6clMCkdW9/56a97bf8-b69b-448f-846c-c3a11683d45a.mp3' },
    { id: 'matilda', label: 'Matilda', description: 'Warm female voice, audiobook style', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/XrExE9yKIg1WjnnlVkGX/b930e18d-6b4d-466e-bab2-0ae97c6d8535.mp3' },
    { id: 'sarah', label: 'Sarah', description: 'Soft, gentle female voice', previewUrl: 'https://storage.googleapis.com/eleven-public-prod/premade/voices/EXAVITQu4vr4xnSDxMaL/01a3e33c-6e99-4ee7-8543-ff2216a32186.mp3' },
];

// Shared audio element for voice previews (only one plays at a time)
let previewAudio = null;

// --- Voice Preview Button Component ---
const VoicePreviewButton = ({ voiceId, voiceList }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    // Use passed voiceList or fallback to static VOICES
    const voice = (voiceList || VOICES).find(v => v.id === voiceId);

    const handleClick = () => {
        if (!voice?.previewUrl) return;

        // Stop any currently playing preview
        if (previewAudio) {
            previewAudio.pause();
            previewAudio.currentTime = 0;
        }

        if (isPlaying) {
            setIsPlaying(false);
            return;
        }

        // Play new preview
        previewAudio = new Audio(voice.previewUrl);
        previewAudio.onended = () => setIsPlaying(false);
        previewAudio.onerror = () => setIsPlaying(false);
        previewAudio.play().catch(() => setIsPlaying(false));
        setIsPlaying(true);
    };

    if (!voice?.previewUrl) return null;

    return (
        <button
            className="voice-preview-btn"
            onClick={handleClick}
            title={isPlaying ? "Stop preview" : `Preview ${voice?.label || 'voice'}`}
            type="button"
        >
            {isPlaying ? <Volume2 size={16} className="playing" /> : <Play size={14} />}
        </button>
    );
};

// --- Import Script Modal ---
const ImportScriptModal = ({ isOpen, onClose, onImport, defaultVoice, availableVoices }) => {
    const [scriptText, setScriptText] = useState('');
    const [selectedVoice, setSelectedVoice] = useState(defaultVoice);

    if (!isOpen) return null;

    const handleImport = () => {
        if (!scriptText.trim()) return;
        onImport(scriptText, selectedVoice);
        setScriptText('');
        onClose();
    };

    const voiceList = availableVoices || VOICES;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Import script</h2>
                    <button className="modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="form-group">
                        <label>Script</label>
                        <textarea
                            className="script-textarea"
                            placeholder="Paste your script here..."
                            value={scriptText}
                            onChange={(e) => setScriptText(e.target.value)}
                            rows={8}
                            autoFocus
                        />
                    </div>

                    <div className="form-group">
                        <label>Voice</label>
                        <div className="voice-selector-row">
                            <select
                                className="voice-select"
                                value={selectedVoice}
                                onChange={(e) => setSelectedVoice(e.target.value)}
                            >
                                {voiceList.map(v => (
                                    <option key={v.id} value={v.id}>{v.label} {v.is_cloned ? ' (Clone)' : ''}</option>
                                ))}
                            </select>
                            <VoicePreviewButton voiceId={selectedVoice} voiceList={voiceList} />
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        className="btn-primary"
                        onClick={handleImport}
                        disabled={!scriptText.trim()}
                    >
                        Import
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Sentence Row Component ---
const SentenceRow = ({ sentence, index, onUpdate, onDelete, onDuplicate, onAddBelow, availableVoices }) => {
    const voiceList = availableVoices || VOICES;

    return (
        <div className="sentence-row">
            <div className="sentence-voice">
                <select
                    value={sentence.voice}
                    onChange={(e) => onUpdate(index, { ...sentence, voice: e.target.value })}
                >
                    {voiceList.map(v => (
                        <option key={v.id} value={v.id}>{v.label} {v.is_cloned ? ' (Clone)' : ''}</option>
                    ))}
                </select>
                <VoicePreviewButton voiceId={sentence.voice} voiceList={voiceList} />
            </div>

            <div className="sentence-text">
                <textarea
                    className="sentence-input-area"
                    value={sentence.text}
                    onChange={(e) => onUpdate(index, { ...sentence, text: e.target.value })}
                    placeholder="Enter sentence..."
                    rows={2}
                />
            </div>

            <div className="sentence-actions">
                <button
                    className="action-btn"
                    onClick={() => onDuplicate(index)}
                    title="Duplicate"
                >
                    <Copy size={16} />
                </button>
                <button
                    className="action-btn"
                    onClick={() => onAddBelow(index)}
                    title="Add below"
                >
                    <Plus size={16} />
                </button>
                <button
                    className="action-btn delete"
                    onClick={() => onDelete(index)}
                    title="Delete"
                >
                    <Trash2 size={16} />
                </button>
            </div>
        </div>
    );
};

// --- Main Component ---
export const RantCreatorPage = ({ session, refreshUsage }) => {
    // Project State
    const [searchParams] = useSearchParams();
    const [projectId, setProjectId] = useState(searchParams.get('project_id'));
    const [loadedProject, setLoadedProject] = useState(null);

    // View mode: 'creator' or 'editor'
    const [viewMode, setViewMode] = useState('creator');

    // Script state
    const [sentences, setSentences] = useState([]);
    const [defaultVoice, setDefaultVoice] = useState('adam');

    // Voice list state
    const [availableVoices, setAvailableVoices] = useState(VOICES);
    const [voicesLoading, setVoicesLoading] = useState(false);

    // Generation state
    const [topic, setTopic] = useState('');
    const [generatedTitle, setGeneratedTitle] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRandomizing, setIsRandomizing] = useState(false);
    const [isCreatingVideo, setIsCreatingVideo] = useState(false);
    const [error, setError] = useState(null);

    // Audio data (after voice generation)
    const [audioData, setAudioData] = useState(null);

    // Modal state
    const [showImportModal, setShowImportModal] = useState(false);

    // Settings state
    const [settings, setSettings] = useState({
        tone: 'angry',
        intensity: 8,
        rating: 'pg13'
    });

    // Load project
    useEffect(() => {
        if (projectId && session?.access_token) {
            loadProject(projectId);
        }
    }, [projectId, session]);

    // Fetch voices
    useEffect(() => {
        const fetchVoices = async () => {
            setVoicesLoading(true);
            try {
                const headers = {};
                if (session?.access_token) {
                    headers['Authorization'] = `Bearer ${session.access_token}`;
                }

                const res = await fetch(`${API_BASE}/api/rant/voices`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    if (data.voices) {
                        setAvailableVoices(data.voices);
                        // If user has a default voice set, use it (logic handled by backend flagging is_default)
                        const userDefault = data.voices.find(v => v.is_default);
                        if (userDefault && !projectId) { // Only set if not loading a project
                            setDefaultVoice(userDefault.id);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch voices:", err);
            } finally {
                setVoicesLoading(false);
            }
        };
        fetchVoices();
    }, [session?.access_token, projectId]);

    const loadProject = async (id) => {
        try {
            const project = await ProjectService.getProject(session.access_token, id);
            if (project && project.data) {
                setLoadedProject(project);
                const data = project.data;

                // Restore Creator State
                if (data.sentences) setSentences(data.sentences);
                if (data.topic) setTopic(data.topic);
                if (data.settings) setSettings(data.settings);
                if (data.defaultVoice) setDefaultVoice(data.defaultVoice);

                // Check if we should go to editor
                if (data.job_id) {
                    setAudioData({ job_id: data.job_id });
                    setGeneratedTitle(data.topic || project.name || "Untitled Rant");
                    setViewMode('editor');
                }
            }
        } catch (err) {
            console.error("Failed to load project:", err);
            // Don't alert here to avoid spamming if just switching pages
        }
    };

    // Parse script text into sentences
    const parseScriptToSentences = (text, voice) => {
        // Split by sentence-ending punctuation, keeping the punctuation
        const parts = text.split(/(?<=[.!?])\s+/);
        return parts
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .map(text => ({ text, voice }));
    };

    // Handle import from modal
    const handleImport = (scriptText, voice) => {
        const newSentences = parseScriptToSentences(scriptText, voice);
        setSentences(newSentences);
    };

    // Handle random topic generation
    const handleRandomTopic = async () => {
        setIsRandomizing(true);
        try {
            const token = session?.access_token;
            const res = await fetch(`${API_BASE}/api/rant/random-topic`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to generate topic');
            const data = await res.json();
            setTopic(data.topic);
        } catch (err) {
            console.error("Failed to get random topic", err);
            // Fallback
            const fallbackTopics = [
                "Why printers never work when you need them",
                "People who clap when the plane lands",
                "Why streaming services are becoming cable TV"
            ];
            setTopic(fallbackTopics[Math.floor(Math.random() * fallbackTopics.length)]);
        } finally {
            setIsRandomizing(false);
        }
    };

    // Generate script from AI
    const handleGenerateScript = async () => {
        if (!topic.trim()) return;

        setIsGenerating(true);
        setError(null);

        try {
            const token = session?.access_token;
            const res = await fetch(`${API_BASE}/api/rant/generate-script`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ topic, settings })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to generate script");

            // Parse the generated script into sentences
            if (data.script) {
                const newSentences = parseScriptToSentences(data.script, defaultVoice);
                setSentences(newSentences);
                if (refreshUsage) refreshUsage();
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setIsGenerating(false);
        }
    };

    // Use sample script
    const handleUseSampleScript = () => {
        const sampleScript = `Camera's on, WiFi's acting up, voice cracks every three words. The teacher calls on me, and suddenly my mind is blanker than my homework folder. I try to unmute, but it's that one second delay, so I just stare. Teacher's like, "Go ahead, we're waiting." Now 32 people are watching me try to remember what a mitochondria is. I finally answer, then forget to mute, and everyone hears my little brother screaming about chicken nuggets in the background. Next class, I'm "having connection issues" on purpose. Subscribe. And like.`;

        const newSentences = parseScriptToSentences(sampleScript, defaultVoice);
        setSentences(newSentences);
    };

    // Sentence operations
    const updateSentence = (index, updated) => {
        const newSentences = [...sentences];
        newSentences[index] = updated;
        setSentences(newSentences);
    };

    const deleteSentence = (index) => {
        setSentences(sentences.filter((_, i) => i !== index));
    };

    const duplicateSentence = (index) => {
        const newSentences = [...sentences];
        newSentences.splice(index + 1, 0, { ...sentences[index] });
        setSentences(newSentences);
    };

    const addSentenceBelow = (index) => {
        const newSentences = [...sentences];
        newSentences.splice(index + 1, 0, { text: '', voice: defaultVoice });
        setSentences(newSentences);
    };

    const clearAllSentences = () => {
        setSentences([]);
    };

    // Create video (voice generation)
    const handleCreateVideo = async () => {
        if (sentences.length === 0) return;

        setIsCreatingVideo(true);
        setError(null);

        try {
            const token = session?.access_token;

            // Generate a unique session ID for idempotency (prevents double-charging on retry)
            const voiceSessionId = crypto.randomUUID ? crypto.randomUUID() :
                `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            const res = await fetch(`${API_BASE}/api/rant/generate-voice`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ sentences, voice_session_id: voiceSessionId })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to generate voice");

            // Store audio data and transition to editor
            console.log("Voice generated:", data);
            setAudioData(data);
            setGeneratedTitle(topic || "Untitled Rant");
            if (refreshUsage) refreshUsage();
            setViewMode('editor');

        } catch (err) {
            console.error(err);
            let userMsg = err.message;
            if (userMsg.toLowerCase().includes('quota') || userMsg.includes('401') || userMsg.includes('credits')) {
                userMsg = "Failed - Not Enough Credits";
            } else if (userMsg.includes('Failed to generate any audio files')) {
                // Check if it was a quota error that got wrapped
                if (userMsg.includes('ElevenLabs quota exceeded')) {
                    userMsg = "Failed - Not Enough Credits";
                } else {
                    userMsg = "Failed - Unknown error, try again";
                }
            } else {
                userMsg = "Failed - Unknown error, try again";
            }
            setError(userMsg);
        } finally {
            setIsCreatingVideo(false);
        }
    };

    // Handle going back from editor to creator
    const handleBackFromEditor = () => {
        setViewMode('creator');
        setAudioData(null);
    };

    // If in editor mode, show the editor
    if (viewMode === 'editor') {
        return (
            <RantEditorPage
                session={session}
                audioData={audioData}
                sentences={sentences}
                title={generatedTitle}
                onBack={handleBackFromEditor}
                initialProject={loadedProject}
                projectId={projectId}
                refreshUsage={refreshUsage}
            />
        );
    }

    return (
        <div className="rant-creator-v2">
            {/* Import Modal */}
            <ImportScriptModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImport={handleImport}
                defaultVoice={defaultVoice}
                availableVoices={availableVoices}
            />

            {/* Main Card */}
            <div className="rant-card">
                {/* Header */}
                <div className="rant-header">
                    <h1>Rants</h1>
                    <p>Turn your hot takes into viral videos</p>
                </div>


                {/* Content */}
                <div className="tab-content">


                    {/* Voice Selection (when no sentences) */}
                    {sentences.length === 0 && (
                        <div className="default-voice-section">
                            <label>
                                <Mic size={16} />
                                Voice Selection
                            </label>
                            <div className="voice-selector-row">
                                <select
                                    value={defaultVoice}
                                    onChange={(e) => setDefaultVoice(e.target.value)}
                                    disabled={voicesLoading}
                                >
                                    {voicesLoading ? (
                                        <option>Loading voices...</option>
                                    ) : (
                                        <>
                                            <optgroup label="Standard Voices">
                                                {availableVoices.filter(v => !v.is_cloned).map(v => (
                                                    <option key={v.id} value={v.id}>{v.label}</option>
                                                ))}
                                            </optgroup>
                                            {availableVoices.some(v => v.is_cloned) && (
                                                <optgroup label="My Cloned Voices">
                                                    {availableVoices.filter(v => v.is_cloned).map(v => (
                                                        <option key={v.id} value={v.id}>{v.label} {v.is_default ? '(Default)' : ''}</option>
                                                    ))}
                                                </optgroup>
                                            )}
                                        </>
                                    )}
                                </select>
                                <VoicePreviewButton voiceId={defaultVoice} voiceList={availableVoices} />
                            </div>
                        </div>
                    )}

                    {/* Sentences Table */}
                    {sentences.length > 0 && (
                        <>
                            <div className="script-controls-header">
                                <button
                                    className="btn-text-danger"
                                    onClick={clearAllSentences}
                                >
                                    <Trash2 size={14} />
                                    Clear Script
                                </button>
                            </div>
                            <div className="sentences-container">
                                <div className="sentences-header">
                                    <div className="col-voice">Voice</div>
                                    <div className="col-sentence">Sentence</div>
                                    <div className="col-actions"></div>
                                </div>

                                <div className="sentences-list">
                                    {sentences.map((sentence, index) => (
                                        <SentenceRow
                                            key={index}
                                            sentence={sentence}
                                            index={index}
                                            onUpdate={updateSentence}
                                            onDelete={deleteSentence}
                                            onDuplicate={duplicateSentence}
                                            onAddBelow={addSentenceBelow}
                                            availableVoices={availableVoices}
                                        />
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Script Generation Section */}
                    {sentences.length === 0 && (
                        <div className="generation-section">
                            <div className="topic-input-row">
                                <textarea
                                    className="topic-input"
                                    placeholder="Enter a topic to rant about... (e.g. 'Why layout shifts ruin the web' or 'People who stand in the aisle as soon as the plane lands')"
                                    value={topic}
                                    onChange={(e) => setTopic(e.target.value)}
                                />
                                <button
                                    className="dice-btn"
                                    onClick={handleRandomTopic}
                                    disabled={isRandomizing}
                                    title="Generate random topic"
                                >
                                    <Dices size={20} className={isRandomizing ? 'spin' : ''} />
                                </button>
                            </div>

                            <div className="generation-settings">
                                <div className="gen-setting">
                                    <label>Tone</label>
                                    <select
                                        value={settings.tone}
                                        onChange={(e) => setSettings({ ...settings, tone: e.target.value })}
                                    >
                                        <option value="angry">Angry</option>
                                        <option value="sarcastic">Sarcastic</option>
                                        <option value="funny">Funny</option>
                                        <option value="educational">Educational</option>
                                        <option value="conspiracy">Conspiracy Theory</option>
                                    </select>
                                </div>

                                <div className="gen-setting">
                                    <label>Intensity: {settings.intensity}</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={settings.intensity}
                                        onChange={(e) => setSettings({ ...settings, intensity: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div className="gen-setting">
                                    <label>Rating</label>
                                    <select
                                        value={settings.rating}
                                        onChange={(e) => setSettings({ ...settings, rating: e.target.value })}
                                    >
                                        <option value="pg">PG (Clean)</option>
                                        <option value="pg13">PG-13 (Edgy)</option>
                                        <option value="r">R (Explicit)</option>
                                    </select>
                                </div>
                            </div>

                            {/* Generate button moved to footer */}
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="error-message">
                            <X size={16} />
                            {error}
                        </div>
                    )}

                    {/* Manual Options (Secondary) */}
                    {sentences.length === 0 ? (
                        <div className="manual-options">
                            <span className="or-divider">OR</span>
                            <div className="manual-buttons">
                                <button
                                    className="btn-secondary"
                                    onClick={() => setShowImportModal(true)}
                                >
                                    Import script
                                </button>
                                <button
                                    className="btn-secondary"
                                    onClick={handleUseSampleScript}
                                >
                                    Use sample script
                                </button>
                            </div>
                            <button
                                className="btn-create-video"
                                onClick={handleGenerateScript}
                                disabled={isGenerating || !topic.trim()}
                                style={{ marginTop: '8px' }}
                            >
                                {isGenerating ? (
                                    <>
                                        <Loader2 size={18} className="spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={18} />
                                        Generate Script
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="action-buttons">
                            <button
                                className="btn-create-video full-width"
                                onClick={handleCreateVideo}
                                disabled={isCreatingVideo}
                            >
                                {isCreatingVideo ? (
                                    <>
                                        <Loader2 size={18} className="spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles size={18} />
                                        Create video
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

